import { CheckCircle2, BedDouble, Bath, Maximize2, MoveUp } from 'lucide-react'
import type { Model } from '@/types'
import { formatCurrency } from '@/lib/pricing'
import { Button } from '@/components/ui/Button'
import { getAssetUrl } from '@/lib/api'

interface Props {
  model: Model
  isSelected: boolean
  onSelect: () => void
}

export function ModelCard({ model, isSelected, onSelect }: Props) {
  return (
    <div
      className={`relative bg-white rounded-xl border-2 transition-all cursor-pointer overflow-hidden ${
        isSelected
          ? 'border-brand-500 shadow-selected'
          : 'border-slate-200 hover:border-slate-300 shadow-card hover:shadow-card-hover'
      }`}
      onClick={onSelect}
    >
      {/* Image */}
      <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
        {model.image_url ? (
          <img src={getAssetUrl(model.image_url)} alt={model.name} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-300 mb-1">{model.name.split(' ')[1]?.[0] ?? 'L'}</div>
              <div className="text-slate-400 text-sm">No render yet</div>
            </div>
          </div>
        )}
        {isSelected && (
          <div className="absolute top-3 right-3 bg-brand-500 rounded-full p-0.5">
            <CheckCircle2 className="h-5 w-5 text-white fill-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h3 className="font-bold text-slate-900 text-lg">{model.name}</h3>
            {model.best_for && (
              <p className="text-xs text-slate-500 mt-0.5">Best for: {model.best_for}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-slate-500">Starting at</div>
            <div className="font-bold text-slate-900">{formatCurrency(model.base_price)}</div>
          </div>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-2">{model.description}</p>

        {/* Specs */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="text-center">
            <Maximize2 className="h-4 w-4 text-slate-400 mx-auto mb-1" />
            <div className="text-xs font-medium text-slate-700">{model.sqft}</div>
            <div className="text-[10px] text-slate-400">sq ft</div>
          </div>
          <div className="text-center">
            <BedDouble className="h-4 w-4 text-slate-400 mx-auto mb-1" />
            <div className="text-xs font-medium text-slate-700">{model.sleeps}</div>
            <div className="text-[10px] text-slate-400">sleeps</div>
          </div>
          <div className="text-center">
            <Bath className="h-4 w-4 text-slate-400 mx-auto mb-1" />
            <div className="text-xs font-medium text-slate-700">{model.bathrooms}</div>
            <div className="text-[10px] text-slate-400">bath</div>
          </div>
          <div className="text-center">
            <MoveUp className="h-4 w-4 text-slate-400 mx-auto mb-1" />
            <div className="text-xs font-medium text-slate-700">{model.has_loft ? 'Yes' : 'No'}</div>
            <div className="text-[10px] text-slate-400">loft</div>
          </div>
        </div>

        <Button
          variant={isSelected ? 'primary' : 'secondary'}
          fullWidth
          size="md"
          onClick={(e) => { e.stopPropagation(); onSelect() }}
        >
          {isSelected ? 'Selected' : 'Build This Model'}
        </Button>
      </div>
    </div>
  )
}
